import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Auth } from "aws-amplify";
import config from "../../../config";
import { getGoogleSignInUrl } from "../../../services/auth/auth-service";
import Message from "../../../components/message/message-comp";
import logo from '../../../assets/images/logo/logo.png';
import glogo from '../../../assets/images/icons/g-logo.png';
import authSideImage from '../../../assets/images/others/auth-side.webp';
import { useQuery } from "../../../helpers/hooks/common-hook";
import FormLoader from "../../../components/form-loader/form-loader";
import AuthTextBox from "../../../components/forms/auth-text-box";
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';
import { onPremSignIn } from '../../../services/auth/on-prem-auth-service';

function SigninPage() {
  const navigate = useNavigate();
  const query = useQuery();
  const signupSuccess = query.get('sign_up');
  const signupAccount = query.get('account');
  const emailQuery = query.get('email');
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [imagePreloaded, setImagePreloaded] = useState(false);

  const [auth, setAuth] = useState({
    username: emailQuery || '',
    password: '',
    rememberMe: false
  });

  const [error, setError] = useState({
    username: false,
    password: false
  });

  const [formState, setFormState] = useState({
    processing: false,
    success: false,
    failed: false,
    message: ''
  });

  // Pre-load image for smoother rendering
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = authSideImage;
    document.head.appendChild(link);

    const img = new Image();
    img.src = authSideImage;
    img.onload = () => {
      setImagePreloaded(true);
    };
    img.onerror = () => {
      setImageFailed(true);
    };

    return () => {
      try {
        document.head.removeChild(link);
      } catch (e) {
        // ignore
      }
    };
  }, []);

  const handleChange = useCallback(event => {
    const targetName = event.target.name;
    const targetValue = event.target.value;

    if (targetName === 'username') {
      auth.username = targetValue;
    } else if (targetName === 'password') {
      auth.password = targetValue;
    }

    if (triedSubmit) {
      validateForm()
    }

    setAuth(Object.assign({}, auth));
  });

  const validateForm = () => {
    let valid = true;
    if (auth.username === '') {
      error.username = true;
      valid = false;
    } else {
      error.username = false;
    }

    if (auth.password === '') {
      error.password = true;
      valid = false;
    } else {
      error.password = false;
    }

    setError(Object.assign({}, error))
    return valid;
  }

  const updateFormState = (processing, success, failed, message) => {
    setFormState(Object.assign({}, { processing, success, failed, message }))
  }

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setTriedSubmit(true);
    if (!formState.processing && validateForm()) {
      updateFormState(true, false, false);
      try {
        if (config.IS_ON_PREM) {
          const response = await onPremSignIn({
            "email": auth.username,
            "password": auth.password
          })
          if (!response) {
            throw new Error('Signin failed. Please check your credentials');
          }
          window.location.href = '/';
        } else {
          await Auth.signIn(auth.username, auth.password);
        }
        updateFormState(false, true, false);
        navigate('/');
      } catch (err) {
        console.error("signin failed cause", err);
        updateFormState(false, false, true, err.message);
      }
    }
  });

  const onGoogleLogin = (event) => {
    event.preventDefault();
    window.location.href = getGoogleSignInUrl();
  }

  return (
    <div className="min-h-screen lg:h-screen grid grid-cols-1 lg:grid-cols-2 bg-white font-sans lg:overflow-hidden">
      <div className="flex flex-col justify-between p-6 sm:p-12 lg:p-16 overflow-y-auto h-screen lg:h-full">
        <div className="flex items-center">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              className="h-8 w-auto object-contain"
              src={logo}
              alt="BotCircuits"
            />
            <span className="text-xl font-bold text-gray-900 tracking-tight">BotCircuits</span>
          </Link>
        </div>

        <div className="my-auto mx-auto w-full max-w-md py-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Sign in to your account</h2>
            <p className="mt-2 text-sm text-gray-600">Reliable AI for Financial Services that Runs Customer Operations</p>
          </div>

          {(signupSuccess && !auth.username) &&
            <div className="mt-4 flex justify-center items-center">
              <Message
                title="You have signed up successfully!"
                text={<span>Please check your <span className="font-extrabold underline">{signupAccount}</span> email to verify account</span>}
                type="success" timeout={0}>
              </Message>
            </div>
          }

          {formState.failed &&
            <div className="mt-4 flex justify-center items-center">
              <Message text={formState.message ? formState.message : 'Signin failed. Please check your credentials'} type="error" />
            </div>
          }

          {formState.processing &&
            <div className="mt-4 flex justify-center items-center">
              <FormLoader />
            </div>
          }

          <div className="mt-6">
            <form className="space-y-4" onSubmit={handleSubmit} method="POST">
              <button
                onClick={onGoogleLogin}
                type="button"
                className="flex w-full justify-center items-center rounded-lg border border-slate-200 bg-white py-2.5 px-4 text-sm font-semibold text-gray-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-lime focus:ring-offset-2 transition-all duration-200"
              >
                <img src={glogo} alt="Google" className="h-5 w-5 mr-3" />
                Sign in with Google
              </button>

              <div className="relative flex py-2 items-center">
                <div className="flex-grow border-t border-slate-200"></div>
                <span className="flex-shrink mx-4 text-xs font-semibold text-gray-400 tracking-wider uppercase">OR</span>
                <div className="flex-grow border-t border-slate-200"></div>
              </div>

              <AuthTextBox
                type="email"
                name="username"
                id="username"
                label="Email"
                value={auth.username}
                placeholder="Enter your email address"
                onChange={handleChange}
                error={error.username}
                autocomplete={true}
              />

              <AuthTextBox
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                label="Password"
                value={auth.password}
                placeholder="Enter password"
                onChange={handleChange}
                error={error.password}
                suffix={
                  showPassword ? (
                    <HiOutlineEye size={20} className="text-gray-500 hover:text-gray-700" onClick={() => setShowPassword(false)} />
                  ) : (
                    <HiOutlineEyeOff size={20} className="text-gray-500 hover:text-gray-700" onClick={() => setShowPassword(true)} />
                  )
                }
              />

              <div className="text-sm">
                <Link to="/forgot-password" className="font-semibold text-primary hover:text-gray-700 underline">
                  Forgot your password?
                </Link>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  className="flex w-full justify-center items-center rounded-lg bg-primary py-3 px-4 text-sm font-semibold text-white shadow-md hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-lime transition-all duration-200 uppercase tracking-wider"
                >
                  Sign in &rarr;
                </button>
              </div>

              {!config.SELF_SIGNUP_DISABLED &&
                <div className="pt-4 text-sm text-center lg:text-left">
                  Don't have an account?{' '}
                  <Link to="/signup" className="font-semibold text-primary hover:text-gray-700 underline">
                    Sign up
                  </Link>
                </div>
              }
            </form>
          </div>
        </div>

        <div className="text-xs text-gray-400 text-center lg:text-left space-x-2">
          <a href="https://botcircuits.ai/privacypolicy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 underline">
            Privacy Policy
          </a>
        </div>
      </div>

      <div className="hidden lg:flex bg-[#FAFAF9] relative items-center justify-center lg:h-full overflow-hidden select-none">
        {!imageFailed ? (
          <img
            src={authSideImage}
            alt="Customer operations that act, not just answer."
            className="w-full h-full object-contain object-left transition-opacity duration-300"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center space-y-4">
            <img src={logo} alt="BotCircuits" className="h-16 w-auto" />
            <span className="text-3xl font-extrabold text-gray-900 tracking-widest uppercase">BotCircuits</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default SigninPage;
